import { api } from './client';
import { parseNotificationData } from '@/lib/notificationKinds';

export type Notification = {
  id: string;
  user_id: string;
  type: string;
  title: string;
  body: string;
  data?: Record<string, unknown>;
  is_read: boolean;
  created_at: string;
};

export type NotificationSettings = {
  email_enabled: boolean;
  push_enabled: boolean;
  /** Экрана у флага нет — храним, чтобы вернуть серверу как было (см. `updateSettings`). */
  telegram_enabled: boolean;
  channels: {
    homework: boolean;
    lessons: boolean;
    payments: boolean;
    speaking: boolean;
    chat: boolean;
  };
};

// Backend Notification model shape (notification_models.go).
type BackendNotification = {
  id: string;
  user_id: string;
  title: string;
  body: string;
  category?: string;
  status?: string;
  is_read: boolean;
  read_at?: string | null;
  created_at: string;
  /**
   * Вид уведомления (`lesson_graded`) — отдельно от категории: по категории
   * работают настройки, и у «куратор оценил урок» она `lessons`. У прочих
   * уведомлений `null`. Договор — `docs/CONTRACTS/lesson-feedback.md`, раздел 5.
   */
  type?: string | null;
  /** Данные уведомления (`lesson_id`, `lesson_title`, `marathon_id`); у прочих `null`. */
  data?: unknown;
  payload?: unknown;
};

// Backend NotificationSetting model shape. `categories` is a JSON string
// (jsonb stored as text) like '{"homework":true,...}'.
type BackendSettings = {
  user_id?: string;
  push_enabled?: boolean;
  email_enabled?: boolean;
  telegram_enabled?: boolean;
  categories?: string;
  updated_at?: string;
};

const DEFAULT_CHANNELS: NotificationSettings['channels'] = {
  homework: true,
  lessons: true,
  payments: true,
  speaking: true,
  chat: true,
};

function adaptNotification(n: BackendNotification): Notification {
  return {
    id: n.id,
    user_id: n.user_id,
    // ⚠️ Сперва `type`, потом категория: у «куратор оценил урок» категория
    // `lessons`, и по ней уведомление не узнало бы себя (сверка с договором 10.09.2026).
    type: n.type || n.category || '',
    title: n.title,
    body: n.body,
    data: parseNotificationData(n.data ?? n.payload),
    is_read: n.is_read,
    created_at: n.created_at,
  };
}

function parseChannels(categories?: string): NotificationSettings['channels'] {
  if (!categories) return { ...DEFAULT_CHANNELS };
  try {
    const parsed = JSON.parse(categories) as Partial<Record<string, boolean>>;
    return {
      homework: parsed.homework ?? DEFAULT_CHANNELS.homework,
      lessons: parsed.lessons ?? DEFAULT_CHANNELS.lessons,
      payments: parsed.payments ?? DEFAULT_CHANNELS.payments,
      speaking: parsed.speaking ?? DEFAULT_CHANNELS.speaking,
      chat: parsed.chat ?? DEFAULT_CHANNELS.chat,
    };
  } catch {
    return { ...DEFAULT_CHANNELS };
  }
}

function adaptSettings(s: BackendSettings): NotificationSettings {
  return {
    email_enabled: s.email_enabled ?? true,
    push_enabled: s.push_enabled ?? true,
    telegram_enabled: s.telegram_enabled ?? false,
    channels: parseChannels(s.categories),
  };
}

export const notificationsApi = {
  async list(offset = 0, limit = 20) {
    const { data } = await api.get('/notifications', { params: { offset, limit } });
    const raw = data as { items: BackendNotification[]; total: number };
    return {
      items: (raw.items ?? []).map(adaptNotification),
      total: raw.total ?? 0,
    } as { items: Notification[]; total: number };
  },
  async unreadCount() {
    const { data } = await api.get('/notifications/unread-count');
    return data as { count: number };
  },
  async markAllRead() {
    await api.post('/notifications/read-all');
  },
  async markRead(id: string) {
    await api.post(`/notifications/read/${id}`);
  },
  async getSettings() {
    const { data } = await api.get('/notifications/settings');
    return adaptSettings(data as BackendSettings);
  },
  async updateSettings(settings: NotificationSettings) {
    // ⚠️ ВСЕГДА ВСЕ ТРИ ФЛАГА (договор `push.md` §3, техдолг 350): флаг, которого нет в теле, сервер запишет
    // `false`. До 01.10.2026 `telegram_enabled` не слался — любое сохранение настроек молча выключало бы его.
    const { data } = await api.put('/notifications/settings', {
      push_enabled: settings.push_enabled,
      email_enabled: settings.email_enabled,
      telegram_enabled: settings.telegram_enabled,
      categories: JSON.stringify(settings.channels),
    });
    return adaptSettings(data as BackendSettings);
  },
};
